/**
 * AuditPage Component
 * AIDLC Administrative Backend
 *
 * Module 06: Audit & History
 * Comprehensive audit log viewer with filtering, search, and session history.
 *
 * Features:
 * - Filterable audit log table (by agent, action, project, date range)
 * - Session history with agent spawn records
 * - Token usage summary
 * - Export capability
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Badge } from '../shared/Badge';
import { Button } from '../shared/Button';
import { Modal } from '../shared/Modal';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import { queryAuditLogs, getSessionsByProject, getSessionById, type AuditQueryParams } from '../../services/auditService';
import type { AuditLogEntry, SessionRecord, AuditAction } from '../../types/audit';
import { useAgents } from '../../hooks/useAgents';

const actionLabels: Record<AuditAction, string> = {
  task_assigned: 'Task Assigned',
  task_completed: 'Task Completed',
  status_changed: 'Status Changed',
  code_reviewed: 'Code Reviewed',
  prompt_queued: 'Prompt Queued',
  prompt_completed: 'Prompt Completed',
  governance_triggered: 'Governance Triggered',
  login: 'Login',
  logout: 'Logout',
  dispatcher_start: 'Dispatcher Started',
  dispatcher_stop: 'Dispatcher Stopped',
  dispatcher_restart: 'Dispatcher Restarted',
  dispatcher_config_changed: 'Dispatcher Config Changed',
  dispatcher_kill_agent: 'Agent Session Killed',
  dispatcher_clear_queue: 'Dispatcher Queue Cleared',
};

/**
 * Who an entry is attributed to. Most entries are about an agent. A dispatcher
 * entry is about something a person did, and records who in its details, so
 * that person is shown rather than "system".
 */
function actorOf(entry: AuditLogEntry, agentName: string | undefined): string {
  if (entry.action.startsWith('dispatcher_')) {
    const { userEmail, userName, userId } = entry.details as { userEmail?: unknown; userName?: unknown; userId?: unknown };
    const who = [userEmail, userName, userId].find((value) => typeof value === 'string' && value);
    if (typeof who === 'string') return who;
  }
  return agentName || entry.agentId;
}

const actionVariants: Record<AuditAction, 'default' | 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  task_assigned: 'info',
  task_completed: 'success',
  status_changed: 'warning',
  code_reviewed: 'info',
  prompt_queued: 'warning',
  prompt_completed: 'success',
  governance_triggered: 'danger',
  login: 'neutral',
  logout: 'neutral',
  dispatcher_start: 'warning',
  dispatcher_stop: 'neutral',
  dispatcher_restart: 'warning',
  dispatcher_config_changed: 'info',
  dispatcher_kill_agent: 'danger',
  dispatcher_clear_queue: 'neutral',
};

export function AuditPage() {
  const { agents } = useAgents(0);
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);
  const [selectedTab, setSelectedTab] = useState<'logs' | 'sessions'>('logs');

  // Filter state
  const [filterAgent, setFilterAgent] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  // Session state
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [selectedSession, setSelectedSession] = useState<SessionRecord | null>(null);

  const loadAuditLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: AuditQueryParams = {};
      if (filterAgent) params.agentId = filterAgent;
      if (filterAction) params.action = filterAction as AuditAction;
      if (filterFrom) params.from = filterFrom;
      if (filterTo) params.to = filterTo;
      const result = await queryAuditLogs(params);
      setEntries(result.entries);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit logs');
    } finally {
      setIsLoading(false);
    }
  }, [filterAgent, filterAction, filterFrom, filterTo]);

  useEffect(() => {
    loadAuditLogs();
  }, [loadAuditLogs]);

  const loadSessions = useCallback(async () => {
    setIsLoadingSessions(true);
    try {
      // Load all sessions (no project filter)
      const result = await queryAuditLogs({});
      // We use sessions endpoint -- get all sessions by passing empty project
      const sessResp = await fetch('/api/sessions');
      if (sessResp.ok) {
        const sessData = await sessResp.json();
        setSessions(Array.isArray(sessData) ? sessData : []);
      }
    } catch (err) {
      // Sessions may not have data yet, that's ok
      setSessions([]);
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTab === 'sessions') {
      loadSessions();
    }
  }, [selectedTab, loadSessions]);

  // Token usage summary from visible entries
  const tokenSummary = useMemo(() => {
    const totalTokens = entries.reduce((sum, e) => sum + (e.tokensUsed || 0), 0);
    const byAgent: Record<string, number> = {};
    entries.forEach((e) => {
      if (e.tokensUsed) {
        byAgent[e.agentId] = (byAgent[e.agentId] || 0) + e.tokensUsed;
      }
    });
    return { totalTokens, byAgent };
  }, [entries]);

  const formatTimestamp = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const clearFilters = () => {
    setFilterAgent('');
    setFilterAction('');
    setFilterFrom('');
    setFilterTo('');
  };

  return (
    <div>
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-ldaja-black">{total}</div>
          <div className="text-xs text-gray-500 mt-1">Total Entries</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-ldaja-blue">
            {tokenSummary.totalTokens.toLocaleString()}
          </div>
          <div className="text-xs text-gray-500 mt-1">Tokens Used</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">
            {entries.filter((e) => e.action === 'task_completed').length}
          </div>
          <div className="text-xs text-gray-500 mt-1">Tasks Completed</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-600">
            {entries.filter((e) => e.action === 'governance_triggered').length}
          </div>
          <div className="text-xs text-gray-500 mt-1">Governance Triggers</div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-700 text-xs">{error}</p>
          <button onClick={() => setError(null)} className="text-red-600 text-xs underline mt-1">Dismiss</button>
        </div>
      )}

      {/* Tab Switcher */}
      <div className="flex items-center gap-1 mb-6 border-b border-gray-200">
        {(['logs', 'sessions'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setSelectedTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              selectedTab === tab
                ? 'border-ldaja-blue text-ldaja-blue'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'logs' ? `Audit Logs (${total})` : `Sessions (${sessions.length})`}
          </button>
        ))}
      </div>

      {/* AUDIT LOGS TAB */}
      {selectedTab === 'logs' && (
        <>
          {/* Filters */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-1">Agent</label>
                <select
                  value={filterAgent}
                  onChange={(e) => setFilterAgent(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-ldaja-blue/50"
                >
                  <option value="">All Agents</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-1">Action</label>
                <select
                  value={filterAction}
                  onChange={(e) => setFilterAction(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-ldaja-blue/50"
                >
                  <option value="">All Actions</option>
                  {Object.entries(actionLabels).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={filterFrom}
                  onChange={(e) => setFilterFrom(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-ldaja-blue/50"
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={filterTo}
                  onChange={(e) => setFilterTo(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-ldaja-blue/50"
                />
              </div>
              <div className="flex items-end gap-2">
                <Button variant="primary" size="sm" onClick={loadAuditLogs} className="flex-1">
                  Filter
                </Button>
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  Clear
                </Button>
              </div>
            </div>
          </div>

          {/* Log Table */}
          {isLoading ? (
            <LoadingSpinner message="Loading audit logs..." />
          ) : entries.length === 0 ? (
            <EmptyState
              title="No audit entries found"
              description="Audit logs will appear here as agents perform actions in the system."
            />
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Timestamp</th>
                    <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Agent</th>
                    <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Action</th>
                    <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Project</th>
                    <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Tokens</th>
                    <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entries.map((entry) => {
                    const agent = agents.find((a) => a.id === entry.agentId);
                    return (
                      <tr
                        key={entry.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => setSelectedEntry(entry)}
                      >
                        <td className="px-4 py-2.5 text-xs text-gray-600 font-mono">
                          {formatTimestamp(entry.timestamp)}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            {agent && (
                              <div
                                className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold"
                                style={{ backgroundColor: agent.avatarColor || '#2563EB' }}
                              >
                                {agent.name.charAt(0)}
                              </div>
                            )}
                            <span className="text-xs text-gray-700">{actorOf(entry, agent?.name)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge variant={actionVariants[entry.action] || 'default'}>
                            {actionLabels[entry.action] || entry.action}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-500">
                          {entry.projectId || '--'}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-600 font-mono">
                          {entry.tokensUsed ? entry.tokensUsed.toLocaleString() : '--'}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-400 max-w-[200px] truncate">
                          {Object.keys(entry.details).length > 0
                            ? JSON.stringify(entry.details).substring(0, 60) + '...'
                            : '--'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* SESSIONS TAB */}
      {selectedTab === 'sessions' && (
        <>
          {isLoadingSessions ? (
            <LoadingSpinner message="Loading sessions..." />
          ) : sessions.length === 0 ? (
            <EmptyState
              title="No sessions recorded"
              description="Session records will appear here as agents are spawned and complete tasks."
            />
          ) : (
            <div className="space-y-3">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow cursor-pointer"
                  onClick={() => setSelectedSession(session)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-sm font-medium text-ldaja-black">Session {session.id.substring(0, 8)}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">Project: {session.projectId}</p>
                    </div>
                    <Badge variant={session.endedAt ? 'neutral' : 'success'} dot>
                      {session.endedAt ? 'Completed' : 'Active'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-[10px] text-gray-500">
                    <span>{session.agentsSpawned.length} agents spawned</span>
                    <span>{session.totalTokens.toLocaleString()} tokens</span>
                    <span>{session.tasksCompleted.length} tasks completed</span>
                    <span>Started: {formatTimestamp(session.startedAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Audit Entry Detail Modal */}
      <Modal
        isOpen={!!selectedEntry}
        onClose={() => setSelectedEntry(null)}
        title="Audit Log Entry"
        size="md"
      >
        {selectedEntry && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] text-gray-400">Timestamp</p>
                <p className="text-xs text-gray-700 font-mono">{formatTimestamp(selectedEntry.timestamp)}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400">Agent</p>
                <p className="text-xs text-gray-700">{actorOf(selectedEntry, undefined)}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400">Action</p>
                <Badge variant={actionVariants[selectedEntry.action] || 'default'}>
                  {actionLabels[selectedEntry.action] || selectedEntry.action}
                </Badge>
              </div>
              <div>
                <p className="text-[10px] text-gray-400">Project</p>
                <p className="text-xs text-gray-700">{selectedEntry.projectId || 'N/A'}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400">Session</p>
                <p className="text-xs text-gray-700">{selectedEntry.sessionId || 'N/A'}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400">Tokens Used</p>
                <p className="text-xs text-gray-700 font-mono">{selectedEntry.tokensUsed?.toLocaleString() || 'N/A'}</p>
              </div>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 mb-1">Details</p>
              <pre className="text-xs text-gray-600 bg-gray-50 rounded p-3 overflow-x-auto font-mono">
                {JSON.stringify(selectedEntry.details, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </Modal>

      {/* Session Detail Modal */}
      <Modal
        isOpen={!!selectedSession}
        onClose={() => setSelectedSession(null)}
        title={`Session ${selectedSession?.id.substring(0, 8) || ''}`}
        size="lg"
      >
        {selectedSession && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-gray-50 rounded p-2">
                <p className="text-[10px] text-gray-400">Total Tokens</p>
                <p className="text-sm font-bold text-ldaja-black">{selectedSession.totalTokens.toLocaleString()}</p>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <p className="text-[10px] text-gray-400">Agents Spawned</p>
                <p className="text-sm font-bold text-ldaja-black">{selectedSession.agentsSpawned.length}</p>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <p className="text-[10px] text-gray-400">Tasks Completed</p>
                <p className="text-sm font-bold text-green-600">{selectedSession.tasksCompleted.length}</p>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <p className="text-[10px] text-gray-400">State Transitions</p>
                <p className="text-sm font-bold text-ldaja-blue">{selectedSession.aidlcStateTransitions.length}</p>
              </div>
            </div>

            {/* Spawned Agents */}
            {selectedSession.agentsSpawned.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 mb-2">Agent Spawn Records</h4>
                <div className="space-y-2">
                  {selectedSession.agentsSpawned.map((spawn, idx) => {
                    const agent = agents.find((a) => a.id === spawn.agentId);
                    return (
                      <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded p-2">
                        {agent && (
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0"
                            style={{ backgroundColor: agent.avatarColor || '#2563EB' }}
                          >
                            {agent.name.charAt(0)}
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="text-xs font-medium text-gray-700">{agent?.name || spawn.agentId}</p>
                          <p className="text-[10px] text-gray-500">{spawn.task}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] text-gray-500">{spawn.tokensUsed.toLocaleString()} tokens</p>
                          <p className="text-[10px] text-gray-400">{spawn.duration}ms</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Summary */}
            {selectedSession.summary && (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 mb-1">Summary</h4>
                <p className="text-xs text-gray-600">{selectedSession.summary}</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
